import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, Lock, Zap, Users, Settings, Gauge, Wrench } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  // Privacy & Security
  {
    category: 'Privacy & Security',
    question: 'Are my files really processed locally in my browser?',
    answer: 'Yes, absolutely! All file processing happens entirely in your browser using client-side JavaScript. When you upload a file, it\'s loaded into your browser\'s memory and processed using Web APIs like Canvas API, File API, and WebAssembly. Your files never leave your device - there are no server uploads, no cloud storage, and no external processing. This client-side approach ensures complete privacy and security for your sensitive documents, images, and personal data.'
  },
  {
    category: 'Privacy & Security',
    question: 'Do you store, log, or track any of my data?',
    answer: 'No, we do not store, log, or track any of your personal data or files. PrivateTools is designed to be completely privacy-first with zero data collection. We don\'t use cookies for tracking, don\'t store files on servers, don\'t log user activity, and don\'t track what tools you use. The only analytics we collect are basic page views for improving the website, but this contains no personal information. Once you close the tab or refresh the page, all data is permanently deleted from browser memory.'
  },
  {
    category: 'Privacy & Security',
    question: 'Can PrivateTools team see what files I upload or process?',
    answer: 'No, we cannot see your files under any circumstances. Since all processing happens locally in your browser using JavaScript, your files never reach our servers. We have zero access to your data - it\'s technically impossible for us to view, access, or intercept your files. This is fundamentally different from other online tools that upload your files to servers for processing.'
  },
  {
    category: 'Privacy & Security',
    question: 'Is it safe to process sensitive documents like contracts, medical records, or financial documents?',
    answer: 'Yes, it\'s completely safe to process sensitive documents with PrivateTools. Since everything happens locally in your browser, sensitive documents like contracts, medical records, tax documents, or financial statements never leave your device. This makes PrivateTools ideal for professionals handling confidential information, businesses with compliance requirements, and anyone concerned about data privacy. However, always ensure you\'re using HTTPS (which we enforce) and keep your browser updated.'
  },
  {
    category: 'Privacy & Security',
    question: 'How does PrivateTools compare to other online tools in terms of privacy?',
    answer: 'Most online tools upload your files to their servers for processing, which poses privacy risks, data breaches potential, and compliance issues. PrivateTools is fundamentally different - we use client-side processing, meaning your files never leave your browser. This approach provides superior privacy protection, eliminates data breach risks, ensures GDPR/HIPAA compliance, works without internet after initial load, and gives you complete control over your data.'
  },
  {
    category: 'Privacy & Security',
    question: 'What happens to my files after I close the browser or tab?',
    answer: 'When you close the browser tab or navigate away from PrivateTools, all your file data is immediately and permanently deleted from browser memory. There\'s no temporary storage, no cache retention, and no recovery possible. This automatic cleanup ensures that even if someone else uses your computer, they cannot access any files you previously processed.'
  },

  // General Usage
  {
    category: 'General Usage',
    question: 'Do I need to create an account or sign up to use PrivateTools?',
    answer: 'No account or registration required! All 40+ tools are completely free and instantly accessible without any signup process. Simply visit any tool page and start using it immediately. This no-registration approach aligns with our privacy-first philosophy - we can\'t track or store user data if we don\'t have user accounts. You can bookmark your favorite tools and access them anytime without any barriers.'
  },
  {
    category: 'General Usage',
    question: 'Are there any file size limits or usage restrictions?',
    answer: 'File size limits are primarily determined by your device\'s available memory (RAM) rather than artificial restrictions. Most tools can handle files up to 100-500MB depending on your device. For reference: mobile devices typically handle 10-50MB files well, desktop browsers can process 100-500MB+ files, and tablets fall somewhere in between. Processing time increases with file size. For very large files, we recommend processing them in smaller batches or using a desktop computer with more RAM.'
  },
  {
    category: 'General Usage',
    question: 'Which browsers and devices are supported?',
    answer: 'PrivateTools works on all modern browsers and devices including: Chrome 80+, Firefox 75+, Safari 13+, Edge 80+, and mobile browsers on iOS Safari and Android Chrome. We support Windows, macOS, Linux, Android, and iOS devices. For the best performance, we recommend using the latest browser version and ensuring JavaScript is enabled. Older browsers may have limited functionality for advanced features.'
  },
  {
    category: 'General Usage',
    question: 'Can I process multiple files at once?',
    answer: 'Yes! Most tools support batch processing for multiple files simultaneously. You can drag and drop multiple files, or use Ctrl+click (Cmd+click on Mac) to select multiple files. The batch processing feature saves significant time when you need to convert, compress, or process many files with the same settings. Each file is processed independently, so if one fails, others will continue processing.'
  },
  {
    category: 'General Usage',
    question: 'How much does PrivateTools cost?',
    answer: 'PrivateTools is completely free to use with no hidden costs, premium tiers, or usage limits. All 40+ tools are available at no charge because we believe privacy-first tools should be accessible to everyone. We don\'t have subscription plans, one-time fees, or pay-per-use charges. The service is supported through minimal, non-intrusive advertisements that don\'t compromise user privacy.'
  },
  {
    category: 'General Usage',
    question: 'Can I use PrivateTools for commercial or business purposes?',
    answer: 'Yes, PrivateTools can be used for commercial and business purposes without any additional licensing fees. Many professionals, agencies, and businesses use our tools for client work, internal processes, and commercial projects. Since all processing happens locally, it\'s perfect for businesses with strict data privacy requirements or compliance needs (GDPR, HIPAA, etc.). However, please review our terms of service for complete usage guidelines.'
  },

  // Technical Questions
  {
    category: 'Technical',
    question: 'Why do some tools take time to load initially?',
    answer: 'Some tools load specialized JavaScript libraries on-demand to optimize performance and reduce initial page load time. For example, PDF tools load PDF processing libraries, image tools load image manipulation libraries, and encryption tools load cryptographic libraries. This is a one-time download per browser session - subsequent uses of the same tool are instant. This lazy loading approach keeps the site fast while providing powerful functionality when needed.'
  },
  {
    category: 'Technical',
    question: 'Can I use PrivateTools offline without internet?',
    answer: 'Partially yes. Once a tool page is fully loaded, basic functionality will work offline because all processing happens in your browser. However, you need an internet connection for: initial page load, downloading tool-specific libraries, loading new tools, and accessing help documentation. We\'re working on Progressive Web App (PWA) features to improve offline functionality in future updates.'
  },
  {
    category: 'Technical',
    question: 'What should I do if a tool fails or shows an error?',
    answer: 'If processing fails, try these troubleshooting steps: 1) Check if your file format is supported (see tool description), 2) Ensure file isn\'t corrupted by opening it in another program, 3) Try with a smaller file to test memory limits, 4) Refresh the page and try again, 5) Try a different browser, 6) Check if browser JavaScript is enabled. Common causes include: unsupported file formats, corrupted files, insufficient device memory, or very large file sizes. Each tool shows specific error messages to help diagnose issues.'
  },
  {
    category: 'Technical',
    question: 'Why is processing slow for large files?',
    answer: 'Processing speed depends on several factors: file size (larger files take longer), device performance (CPU and RAM), tool complexity (image processing vs text conversion), and browser efficiency. To improve speed: close other browser tabs, use a desktop computer for large files, ensure sufficient RAM is available, try processing files in smaller batches, and use the latest browser version. Processing happens locally, so it\'s limited by your device\'s capabilities rather than internet speed.'
  },
  {
    category: 'Technical',
    question: 'What file formats are supported by different tools?',
    answer: 'Each tool supports specific formats: Image tools (JPG, PNG, WEBP, GIF, BMP, SVG), PDF tools (PDF, images for PDF creation), Text tools (TXT, plain text input), Encryption tools (any text input), Developer tools (JSON, XML, CSV, Markdown), Converter tools (various number formats, text). Specific format support is listed on each tool page. We regularly add support for new formats based on user requests.'
  },
  {
    category: 'Technical',
    question: 'How secure is the client-side processing?',
    answer: 'Client-side processing is inherently more secure than server-based processing because: your data never travels over the internet, there\'s no risk of server breaches, no man-in-the-middle attacks on your files, no storage on potentially compromised servers, and you maintain complete control. We use standard Web APIs and established JavaScript libraries with security best practices. However, ensure you\'re on the official PrivateTools website (check URL) and keep your browser updated.'
  },

  // Tool-Specific Questions
  {
    category: 'Tool-Specific',
    question: 'How do I choose the right image compression settings?',
    answer: 'For image compression, consider your use case: Web use (70-80% quality for good balance), Print materials (90-95% quality for high quality), Social media (60-70% quality, smaller file sizes), Email attachments (50-60% quality for size limits), Profile pictures (70-80% quality). Start with 80% quality and adjust based on file size needs. Preview before downloading to ensure acceptable quality. Remember: lower quality = smaller file size but reduced image quality.'
  },
  {
    category: 'Tool-Specific',
    question: 'What\'s the difference between PNG and JPG conversion?',
    answer: 'PNG and JPG serve different purposes: PNG supports transparency, has lossless compression, larger file sizes, perfect for logos/graphics with transparency, and supports fewer colors efficiently. JPG has no transparency support, uses lossy compression, smaller file sizes, ideal for photographs, and handles millions of colors well. Convert PNG to JPG for smaller photo files (transparency becomes white), and JPG to PNG when you need transparency or higher quality for graphics.'
  },
  {
    category: 'Tool-Specific',
    question: 'How do PDF merging and splitting tools work?',
    answer: 'PDF tools use PDF-lib library for client-side processing: Merging combines multiple PDFs into one while preserving formatting, bookmarks, and metadata. You can reorder files by dragging before merging. Splitting extracts specific pages or page ranges from PDFs. All PDF operations maintain original quality and don\'t compress content. For password-protected PDFs, you\'ll need to provide the password. Very large PDFs may require more processing time depending on your device.'
  },
  {
    category: 'Tool-Specific',
    question: 'Which text tools are best for different use cases?',
    answer: 'Different text tools serve specific purposes: Text Analyzer for content statistics and readability analysis, Case Converters for formatting consistency (uppercase, lowercase, camelCase), Duplicate Line Remover for cleaning up lists and data, Text Sorter for organizing lists alphabetically or numerically, Bionic Reading for improved reading speed and focus. Choose based on your specific text processing needs. Many tools can be used in combination for complex text processing workflows.'
  },
  {
    category: 'Tool-Specific',
    question: 'How secure are the encryption and hashing tools?',
    answer: 'Our encryption tools use standard, well-tested algorithms: MD5/SHA hashing for checksums and verification (note: MD5 is not cryptographically secure), SHA-256/SHA-512 for secure hashing, Base64 for encoding (not encryption), URL encoding for web-safe strings. All processing happens locally using established crypto libraries. Remember: hashing is one-way (cannot be reversed), Base64 is encoding (can be decoded), and these tools are for standard use cases, not high-security applications.'
  },

  // Performance & Optimization
  {
    category: 'Performance',
    question: 'How can I improve tool performance on my device?',
    answer: 'To optimize performance: close unnecessary browser tabs and applications, ensure sufficient RAM is available (4GB+ recommended for large files), use the latest browser version, disable browser extensions that might interfere, process files in smaller batches, use desktop computers for heavy processing, clear browser cache if experiencing issues, and ensure stable internet for initial tool loading. Performance scales with device capabilities since all processing is local.'
  },
  {
    category: 'Performance',
    question: 'Why does my browser slow down when processing large files?',
    answer: 'Large file processing is memory-intensive because the entire file is loaded into browser memory (RAM) for processing. This is normal behavior for client-side processing. To mitigate: ensure sufficient RAM (8GB+ for large files), close other applications and browser tabs, process files in smaller batches, use desktop computers for heavy work, and be patient with large files (processing time scales with file size). The browser may temporarily become less responsive during processing.'
  },
  {
    category: 'Performance',
    question: 'What are the recommended system requirements?',
    answer: 'Minimum requirements: 2GB RAM, modern browser (Chrome 80+, Firefox 75+, Safari 13+, Edge 80+), JavaScript enabled, and stable internet for initial loading. Recommended: 8GB+ RAM for large files, desktop/laptop for heavy processing, latest browser version, and fast CPU for quicker processing. Mobile devices work well for smaller files (under 50MB), while desktop computers handle large files (100MB+) more efficiently.'
  },

  // Features & Development
  {
    category: 'Features',
    question: 'How often do you add new tools and features?',
    answer: 'We release new tools and features monthly based on user feedback, trending needs, and technological advances. Recent additions include enhanced image processing, new text utilities, and improved PDF tools. We prioritize tools that: solve common problems, maintain privacy-first principles, work reliably across devices, and complement existing functionality. You can request specific tools through our contact form, and popular requests are prioritized in our development roadmap.'
  },
  {
    category: 'Features',
    question: 'Can I suggest new tools or improvements?',
    answer: 'Absolutely! User feedback drives our development priorities. Use our contact form to suggest: new tools for specific use cases, improvements to existing tools, additional file format support, user interface enhancements, performance optimizations, or accessibility features. Include details about your use case, expected functionality, and why it would benefit other users. We review all suggestions and often implement the most requested features.'
  },
  {
    category: 'Features',
    question: 'Are there plans for mobile apps or desktop applications?',
    answer: 'Currently, PrivateTools is a web-based platform optimized for all devices through responsive design. Our mobile web experience provides full functionality without app installation. We\'re evaluating Progressive Web App (PWA) features for better offline support and native-like experience. Desktop applications aren\'t currently planned since the web platform provides cross-platform compatibility and automatic updates. Future decisions depend on user demand and technological benefits.'
  },
  {
    category: 'Features',
    question: 'Do you offer API access or developer integrations?',
    answer: 'Currently, PrivateTools is designed as a user-facing web application without API access. However, many of our tools are built with open-source libraries that developers can use independently: PDF-lib for PDF processing, Sharp.js for image processing, CryptoJS for encryption, and various text processing libraries. If you need programmatic access to similar functionality, consider using these underlying libraries directly in your applications.'
  }
];

export const FAQPage: React.FC = () => {
  const [openItem, setOpenItem] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', ...Array.from(new Set(faqData.map(item => item.category)))];
  
  // SEO optimization - count FAQs by category
  const categoryStats = categories.reduce((acc, cat) => {
    if (cat === 'All') {
      acc[cat] = faqData.length;
    } else {
      acc[cat] = faqData.filter(item => item.category === cat).length;
    }
    return acc;
  }, {} as Record<string, number>);
  
  const filteredFAQ = selectedCategory === 'All' 
    ? faqData 
    : faqData.filter(item => item.category === selectedCategory);

  const toggleItem = (index: number) => {
    setOpenItem(openItem === index ? null : index);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Privacy & Security': return <Shield className="h-5 w-5" />;
      case 'General Usage': return <Users className="h-5 w-5" />;
      case 'Technical': return <Zap className="h-5 w-5" />;
      case 'Features': return <Lock className="h-5 w-5" />;
      case 'Tool-Specific': return <Wrench className="h-5 w-5" />;
      case 'Performance': return <Gauge className="h-5 w-5" />;
      default: return <Settings className="h-5 w-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Frequently Asked Questions - PrivateTools Help Center
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-6">
            Find comprehensive answers to common questions about PrivateTools, our privacy practices, 
            technical details, and how to get the most out of our 40+ free online tools.
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{categoryStats['All']}</div>
              <div className="text-sm text-blue-700 dark:text-blue-400">Total FAQs</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{categoryStats['Privacy & Security']}</div>
              <div className="text-sm text-green-700 dark:text-green-400">Privacy Q&As</div>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{categoryStats['Tool-Specific']}</div>
              <div className="text-sm text-purple-700 dark:text-purple-400">Tool Guides</div>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{categoryStats['Technical']}</div>
              <div className="text-sm text-orange-700 dark:text-orange-400">Technical Help</div>
            </div>
          </div>

          {/* SEO Content */}
          <div className="text-left max-w-4xl mx-auto mb-8 bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">About This FAQ</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
              This comprehensive FAQ covers everything you need to know about using PrivateTools - the leading 
              privacy-first online tool platform. Whether you're looking for help with image compression, PDF merging, 
              text analysis, encryption tools, or understanding our privacy practices, you'll find detailed answers here. 
              All our tools process files locally in your browser, ensuring complete privacy and security for your data.
            </p>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-4">
            Browse FAQ Categories
          </h2>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <span>{category}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  selectedCategory === category
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                  {categoryStats[category]}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {filteredFAQ.map((item, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="text-blue-600 dark:text-blue-400">
                    {getCategoryIcon(item.category)}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {item.question}
                  </h3>
                </div>
                <div className="text-gray-500 dark:text-gray-400">
                  {openItem === index ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </div>
              </button>
              
              {openItem === index && (
                <div className="px-6 pb-4">
                  <div className="pl-8">
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-8 border border-blue-200 dark:border-blue-800">
            <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-400 mb-4">
              Still have questions?
            </h3>
            <p className="text-blue-700 dark:text-blue-400 mb-6">
              Can't find what you're looking for? We're here to help!
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>

        {/* Additional SEO Content */}
        <div className="mt-12 space-y-8">
          {/* Tool Categories Overview */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              PrivateTools Categories - Quick Reference
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">🖼️ Image Tools</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Convert between formats (WEBP, PNG, JPG), compress images, crop and resize, 
                  color inversion, and Base64 conversion.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">📄 PDF Tools</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Merge multiple PDFs, convert images to PDF, extract PDF metadata, 
                  and convert text documents to PDF.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">📝 Text Tools</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Text analysis, case conversion, duplicate removal, text sorting, 
                  and bionic reading enhancement.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">🔐 Encryption Tools</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Hash generation (MD5, SHA1, SHA256, SHA512), Base64 encoding/decoding, 
                  URL encoding, and HTML encoding.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">💻 Developer Tools</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  JSON/CSV conversion, XML formatting, Markdown processing, 
                  and network utilities for developers.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">🔄 Converter Tools</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Number base conversion, QR code generation, timezone conversion, 
                  and various format transformations.
                </p>
              </div>
            </div>
          </div>

          {/* Common Use Cases */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-8 border border-blue-200 dark:border-blue-800">
            <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-400 mb-6 text-center">
              Popular Use Cases for PrivateTools
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-3">For Professionals</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
                  <li>• Process confidential documents without cloud upload</li>
                  <li>• Compress images for web without quality loss</li>
                  <li>• Merge contracts and legal documents securely</li>
                  <li>• Convert formats for client deliverables</li>
                  <li>• Analyze text content for reports</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-3">For Students & Educators</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
                  <li>• Convert assignments between different formats</li>
                  <li>• Compress images for presentations</li>
                  <li>• Merge research documents into single PDFs</li>
                  <li>• Analyze text for word count and readability</li>
                  <li>• Create QR codes for educational resources</li>
                </ul>
              </div>
            </div>
          </div>

          {/* SEO Keywords Section */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-8 border border-purple-200 dark:border-purple-800">
            <h2 className="text-2xl font-bold text-purple-900 dark:text-purple-400 mb-6 text-center">
              Why Choose PrivateTools?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl mb-2">🔒</div>
                <h3 className="font-semibold text-purple-900 dark:text-purple-400 mb-2">100% Private</h3>
                <p className="text-sm text-purple-800 dark:text-purple-400">
                  All processing happens locally in your browser. No uploads to servers.
                </p>
              </div>
              <div>
                <div className="text-3xl mb-2">⚡</div>
                <h3 className="font-semibold text-purple-900 dark:text-purple-400 mb-2">Fast Processing</h3>
                <p className="text-sm text-purple-800 dark:text-purple-400">
                  Client-side processing means faster results and no waiting for uploads.
                </p>
              </div>
              <div>
                <div className="text-3xl mb-2">🆓</div>
                <h3 className="font-semibold text-purple-900 dark:text-purple-400 mb-2">Completely Free</h3>
                <p className="text-sm text-purple-800 dark:text-purple-400">
                  All 40+ tools are free with no registration or hidden costs.
                </p>
              </div>
              <div>
                <div className="text-3xl mb-2">🌐</div>
                <h3 className="font-semibold text-purple-900 dark:text-purple-400 mb-2">Cross-Platform</h3>
                <p className="text-sm text-purple-800 dark:text-purple-400">
                  Works on all devices and browsers without app installation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="mt-8 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-green-800 dark:text-green-400 text-sm text-center">
            🔒 Your privacy is our priority. All tools process data locally in your browser - no uploads, no storage, no tracking.
          </p>
        </div>
      </div>
    </div>
  );
};